import React, { useEffect, useState, useRef } from 'react';
import { fetchDashboardData } from '../services/kioskApi';
import { Tile, DeviceTileCard } from './shared/DashboardTiles';
import ErrorBanner from './shared/ErrorBanner';
import DeviceHistoryChart from './shared/DeviceHistoryChart';
import { useAuth } from '../context/AuthContext';
import { fetchSetpointDeviations, Deviation } from '../services/api';


interface DashboardData {
    title: string;
    refresh_sec: number;
    fetched_at: string;
    tiles: Tile[];
}

interface ChartState {
    deviceIeee: string;
    deviceName: string;
    property: string;
    propertyLabel: string;
}

const Dashboard: React.FC = () => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [chartState, setChartState] = useState<ChartState | null>(null);
    // Deviations (avvikelse) over the last 24 h, keyed by `${ieee}::${parameter_key}`.
    const [deviations, setDeviations] = useState<Record<string, Deviation>>({});
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const auth = useAuth();

    const fetchData = async () => {
        try {
            const dashboard = await fetchDashboardData();
            setData(dashboard);
            setError(null);

            // Best-effort: pull 24 h deviations for every device on the dashboard.
            const ieees = (dashboard?.tiles ?? [])
                .map((t: Tile) => t.device_ieee)
                .filter(Boolean);
            if (ieees.length > 0) {
                try {
                    const devs = await fetchSetpointDeviations(ieees, 24);
                    const map: Record<string, Deviation> = {};
                    for (const d of devs) map[`${d.ieee_address}::${d.parameter_key}`] = d;
                    setDeviations(map);
                } catch { /* deviations are optional — ignore */ }
            }
        } catch (e: any) {
            const errorMessage = e?.response?.data?.message || e.message || 'Kunde inte hämta översikten';
            setError(errorMessage);
            console.error('Dashboard load error:', e);
        }
    };

    // Initial fetch + auto-refresh
    useEffect(() => {
        fetchData();
        
        const scheduleNext = (sec: number) => {
            timerRef.current = setTimeout(() => {
                fetchData().then(() => {
                    scheduleNext(data?.refresh_sec ?? sec);
                });
            }, sec * 1000);
        };
        scheduleNext(data?.refresh_sec ?? 30);
        
        return () => { 
            if (timerRef.current) clearTimeout(timerRef.current); 
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastUpdated = data?.fetched_at
        ? new Date(data.fetched_at).toLocaleTimeString('sv-SE')
        : null;

    const handleReadingClick = (deviceIeee: string, deviceName: string, property: string, propertyLabel: string) => {
        setChartState({ deviceIeee, deviceName, property, propertyLabel });
    };

    return (
        <div>
            {/* Error banner */}
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '20px',
                padding: '14px 16px',
                background: '#ffffff',
                borderRadius: '10px',
                boxShadow: '0 1px 4px rgba(0,0,0,.06)',
            }}>
                <h2 style={{ margin: 0, fontSize: 'clamp(18px, 5vw, 22px)', fontWeight: 700, color: '#1a202c' }}>
                    {data?.title || 'Översikt'}
                </h2>
                {lastUpdated && (
                    <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        Uppdaterad: {lastUpdated}
                    </span>
                )}
            </div>

            {/* Empty state */}
            {data && data.tiles.length === 0 && !error && (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#ffffff',
                    borderRadius: '10px',
                    boxShadow: '0 1px 4px rgba(0,0,0,.06)',
                }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                    <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
                        Inga enheter konfigurerade. Gå till <strong>Kiosk-admin</strong> för att lägga till sensorer och gateways.
                    </p>
                </div>
            )}

            {/* Tiles grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                gap: 16,
            }}>
                {data?.tiles.map(tile => (
                    <DeviceTileCard
                        key={tile.device_ieee}
                        tile={tile}
                        dark={false}
                        onReadingClick={handleReadingClick}
                        deviations={deviations}
                    />
                ))}
            </div>

            {/* Chart modal */}
            {chartState && (
                <DeviceHistoryChart
                    deviceIeee={chartState.deviceIeee}
                    deviceName={chartState.deviceName}
                    property={chartState.property}
                    propertyLabel={chartState.propertyLabel}
                    timezone={auth?.user?.timezone || 'Europe/Stockholm'}
                    onClose={() => setChartState(null)}
                />
            )}
        </div>
    );
};

export default Dashboard;